import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";

import { ErrorBoundary } from "@app/components/errors/error-boundary";
import { LazyCurateOutletAppProviders } from "@app/federation-imports/curate/outlets/app-providers";
import { LazyCurateOutletProjectPermission } from "@app/federation-imports/curate/outlets/project-permission";
import { LazyCurateOutletStateProvider } from "@app/federation-imports/curate/outlets/state-provider";
import { LazyCuratePageAccount } from "@app/federation-imports/curate/pages/account";
import { LazyCuratePageProjectDetail } from "@app/federation-imports/curate/pages/project-detail";
import { LazyCuratePageProjectFileExportRedirect } from "@app/federation-imports/curate/pages/project-file-export-redirect";
import { LazyCuratePageProjectList } from "@app/federation-imports/curate/pages/project-list";
import { LazyCuratePageProjectProducts } from "@app/federation-imports/curate/pages/project-products";
import { LazyCuratePageProjectScenarioExportRedirect } from "@app/federation-imports/curate/pages/project-scenario-export-redirect";
import { LazyCuratePageProjectScenarioRules } from "@app/federation-imports/curate/pages/project-scenario-rules";
// TODO ::: update settings page to reflect SSDI only projects
// import { LazyCuratePageProjectSettings } from "@app/federation-imports/curate/pages/project-settings";
import { LazyCuratePageReviewAssortment } from "@app/federation-imports/curate/pages/review-assortment";
import { LazyCuratePageReviewPlanogramCluster } from "@app/federation-imports/curate/pages/review-planogram-cluster";
import { LazyCuratePageReviewPlanogramStore } from "@app/federation-imports/curate/pages/review-planogram-store";
import { LazyCuratePageReviewStoreList } from "@app/federation-imports/curate/pages/review-store-list";
import { LazyCuratePageScenarioProvider } from "@app/federation-imports/curate/pages/scenario-provider";
import { LazyCurateHomeOutletAppPermission } from "@app/federation-imports/curate-home/outlets/app-permission";
import { LazyCurateHomePageHome } from "@app/federation-imports/curate-home/pages/home";
import { LazyExperimentalLoaderProjectDetail } from "@app/federation-imports/experimental/loaders/project-detail";
import { LazyExperimentalLoaderScenario } from "@app/federation-imports/experimental/loaders/scenario";
import { LazyExperimentalOutletAppPermission } from "@app/federation-imports/experimental/outlets/app-permission";
import { LazyExperimentalPageLanding } from "@app/federation-imports/experimental/pages/landing";
import { LazyExperimentalPageProjectDetail } from "@app/federation-imports/experimental/pages/project-detail";
import { LazyExperimentalPageProjectScenario } from "@app/federation-imports/experimental/pages/project-scenario";
import { LazyExperimentalPageProjectScenarioStore } from "@app/federation-imports/experimental/pages/project-scenario-store";
import { LazyExperimentalPageProjectStrategies } from "@app/federation-imports/experimental/pages/project-strategies";
import { LazyExperimentalPageProjects } from "@app/federation-imports/experimental/pages/projects";
import { LazyHomeLayoutShell } from "@app/federation-imports/home/layouts/shell";
import { LazyHomePageHome } from "@app/federation-imports/home/pages/home";
import { LazySSDIPageDatasetCreate } from "@app/federation-imports/ssdi/pages/dataset-create";
import { LazySSDIPageDatasetList } from "@app/federation-imports/ssdi/pages/dataset-list";
import { LazySSDIPageDatasetValidate } from "@app/federation-imports/ssdi/pages/dataset-validate";
import { NavigationLayout } from "@app/layouts";
import { PageLogin } from "@app/pages";
import { getAppPaths, getUxMode } from "@app/routes/app-paths";
import { AuthProvider } from "@app/routes/outlets/auth-provider";
import { UserLoader } from "@app/routes/outlets/user-loader";

export const wrapWithNavLayout = (route: RouteObject): RouteObject => {
  const NavWrapper = route.element ? <NavigationLayout>{route.element}</NavigationLayout> : <NavigationLayout />;

  return {
    ...route,
    element: NavWrapper,
  };
};

export const wrapAllWithNavLayout = (route: Array<RouteObject>): Array<RouteObject> => {
  return route.map(wrapWithNavLayout);
};

export const routes = (): RouteObject => {
  const uxMode = getUxMode();
  const appPaths = getAppPaths();
  let homeRouter: RouteObject = {
    path: appPaths.home,
    element: (
      <NavigationLayout>
        <LazyCurateHomeOutletAppPermission />
      </NavigationLayout>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <LazyCurateHomePageHome />,
      },
    ],
  };

  switch (uxMode) {
    case "sentinel":
      homeRouter = {
        path: appPaths.home,
        element: <LazyHomeLayoutShell />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            element: <LazyHomePageHome />,
          },
        ],
      };
      break;
  }

  return {
    path: "/",
    errorElement: <ErrorBoundary />,
    element: (
      <AuthProvider>
        <UserLoader />
      </AuthProvider>
    ),
    children: [
      {
        path: appPaths.login,
        errorElement: <ErrorBoundary />,
        element: <PageLogin />,
      },
      homeRouter,
      {
        path: appPaths.projects,
        element: (
          <NavigationLayout>
            <LazyCurateOutletStateProvider>
              <LazyCurateOutletAppProviders />
            </LazyCurateOutletStateProvider>
          </NavigationLayout>
        ),
        children: [
          {
            index: true,
            element: <LazyCuratePageProjectList />,
          },
          {
            path: ":pId",
            element: <LazyCurateOutletProjectPermission />,
            children: [
              {
                index: true,
                element: <LazyCuratePageProjectDetail />,
              },
              // {
              //   path: "settings",
              //   element: <LazyCuratePageProjectSettings />,
              // },
              {
                path: "reviews/:reviewId",
                children: [
                  {
                    index: true,
                    element: <LazyCuratePageReviewStoreList />,
                  },
                  {
                    path: "product/:productId",
                    element: <LazyCuratePageProjectProducts />,
                  },
                  {
                    path: "store-planogram/:storeId",
                    element: <LazyCuratePageReviewPlanogramStore />,
                  },
                  {
                    path: "cluster-planogram/:clusterId",
                    element: <LazyCuratePageReviewPlanogramCluster />,
                  },
                  {
                    path: "bulk-edit",
                    element: <LazyCuratePageReviewAssortment />,
                  },
                  {
                    path: "export-redirect/:eId",
                    element: <LazyCuratePageProjectFileExportRedirect />,
                  },
                ],
              },
              {
                path: "scenarios/:sId",
                children: [
                  {
                    index: true,
                    element: <LazyCuratePageScenarioProvider />,
                  },
                  {
                    path: "scenario-export-redirect",
                    element: <LazyCuratePageProjectScenarioExportRedirect />,
                  },
                  {
                    path: "rules/:rId",
                    element: <LazyCuratePageProjectScenarioRules />,
                  },
                ],
              },
              {
                path: ":sId",
                children: [
                  {
                    index: true,
                    loader: ({ request }) => {
                      const [projectId, scenarioId] = request.url.split("projects/")[1].split("/");

                      return redirect(`/${appPaths.projects}/${projectId}/scenarios/${scenarioId}`);
                    },
                  },
                  {
                    path: ":rId",
                    loader: ({ request }) => {
                      const [projectId, scenarioId, ruleId] = request.url.split("projects/")[1].split("/");

                      return redirect(`/${appPaths.projects}/${projectId}/scenarios/${scenarioId}/rules/${ruleId}`);
                    },
                  },
                  {
                    path: "export-redirect/:eId",
                    element: <LazyCuratePageProjectFileExportRedirect />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: appPaths.account,
        element: (
          <NavigationLayout>
            <LazyCurateOutletStateProvider>
              <LazyCurateOutletAppProviders />
            </LazyCurateOutletStateProvider>
          </NavigationLayout>
        ),
        children: [
          {
            index: true,
            element: <LazyCuratePageAccount />,
          },
        ],
      },
      {
        path: appPaths.ssdi,
        errorElement: <ErrorBoundary />,
        element: <NavigationLayout />,
        children: [
          {
            index: true,
            element: <LazySSDIPageDatasetList />,
          },
          {
            path: "self-service",
            children: [
              {
                path: ":datasetId/create",
                element: <LazySSDIPageDatasetCreate />,
              },
              {
                path: ":datasetId/validate",
                element: <LazySSDIPageDatasetValidate />,
              },
            ],
          },
        ],
      },
      {
        path: appPaths.experimental,
        errorElement: <ErrorBoundary />,
        element: <LazyExperimentalOutletAppPermission />,
        children: [
          {
            index: true,
            loader: () => redirect(`/${appPaths.experimental}/landing`),
          },
          {
            path: "landing",
            children: [
              {
                index: true,
                element: <LazyExperimentalPageLanding />,
              },
            ],
          },
          {
            path: "projects",
            children: [
              {
                index: true,
                element: <LazyExperimentalPageProjects />,
              },
              {
                path: ":projectId",
                element: <LazyExperimentalLoaderProjectDetail />,
                children: [
                  {
                    index: true,
                    element: <LazyExperimentalPageProjectDetail />,
                  },
                  {
                    path: "scenarios/:scenarioId",
                    element: <LazyExperimentalLoaderScenario />,
                    children: [
                      {
                        index: true,
                        element: <LazyExperimentalPageProjectScenario />,
                      },
                      {
                        path: "stores/:storeNumber",
                        children: [
                          {
                            index: true,
                            element: <LazyExperimentalPageProjectScenarioStore />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "strategies/:strategyId",
                    children: [
                      {
                        index: true,
                        element: <LazyExperimentalPageProjectStrategies />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "*",
            loader: () => redirect(`/${appPaths.experimental}/landing`),
          },
        ],
      },
      {
        path: "*",
        loader: (data) => {
          console.warn("unknown path", data);
          return redirect(`/${appPaths.login}`);
        },
      },
    ],
  };
};
